import React, { useEffect, useRef } from 'react';
import { CHAT } from '@/constants/testIds';

// Black sparkle rain on a fixed canvas overlay. Pointer-events disabled,
// pauses when tab hidden, respects prefers-reduced-motion.
export const SparkleRainCanvas = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  const hiddenRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    const COUNT = reduced ? 30 : isMobile ? 90 : 140;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      sizeRef.current = { w, h, dpr };
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function spawn(initial) {
      const { w, h } = sizeRef.current;
      return {
        x: Math.random() * w,
        y: initial ? Math.random() * h : -10 - Math.random() * 200,
        size: 1.2 + Math.random() * 1.4,
        vy: 0.6 + Math.random() * 1.0,
        vx: -0.25 + Math.random() * 0.5,
        twPhase: Math.random() * Math.PI * 2,
        twSpeed: 0.02 + Math.random() * 0.04,
        rot: Math.random() * Math.PI,
        rotSpeed: -0.02 + Math.random() * 0.04,
      };
    }

    resize();
    particlesRef.current = Array.from({ length: COUNT }, () => spawn(true));

    function drawSparkle(p) {
      const tw = 0.55 + 0.4 * Math.sin(p.twPhase);
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = tw;
      ctx.fillStyle = '#000';
      // 4-pointed sparkle (astroid / Steelmark-style) — quadratic Béziers
      // curve inward between each tip to give the classic concave-star look.
      const r = p.size * 1.8;
      const c = r * 0.18; // smaller = more pointy spikes
      ctx.beginPath();
      ctx.moveTo(0, -r);
      ctx.quadraticCurveTo(c, -c, r, 0);
      ctx.quadraticCurveTo(c, c, 0, r);
      ctx.quadraticCurveTo(-c, c, -r, 0);
      ctx.quadraticCurveTo(-c, -c, 0, -r);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function step() {
      if (hiddenRef.current) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }
      const { w, h } = sizeRef.current;
      ctx.clearRect(0, 0, w, h);
      const ps = particlesRef.current;
      for (let i = 0; i < ps.length; i += 1) {
        const p = ps[i];
        p.x += p.vx;
        p.y += p.vy;
        p.twPhase += p.twSpeed;
        p.rot += p.rotSpeed;
        if (p.y > h + 20 || p.x < -20 || p.x > w + 20) {
          ps[i] = spawn(false);
        } else {
          drawSparkle(p);
        }
      }
      rafRef.current = requestAnimationFrame(step);
    }

    if (reduced) {
      // Render one static frame so it looks intentional
      const ps = particlesRef.current;
      ps.forEach((p) => drawSparkle(p));
    } else {
      rafRef.current = requestAnimationFrame(step);
    }

    const onResize = () => resize();
    const onVis = () => {
      hiddenRef.current = document.hidden;
    };
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="sparkle-rain"
      aria-hidden="true"
      data-testid={CHAT.sparkleCanvas}
    />
  );
};
